import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Plus, Trash2, GripVertical, Pencil, Lock, FileText, Link as LinkIcon, File } from 'lucide-react';
import { type OptinConfig, OptinType, queueApiService } from '@/services/queueApi.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface OptinsBuilderProps {
  optins: OptinConfig[];
  onChange: (optins: OptinConfig[]) => void;
  eventId: string;
}

const TYPE_LABELS: Record<OptinType, string> = {
  [OptinType.TEXT]: 'Texto',
  [OptinType.LINK]: 'Link',
  [OptinType.DOCUMENT]: 'Documento',
};

const TYPE_ICONS: Record<OptinType, React.ReactNode> = {
  [OptinType.TEXT]: <FileText className="w-3 h-3" />,
  [OptinType.LINK]: <LinkIcon className="w-3 h-3" />,
  [OptinType.DOCUMENT]: <File className="w-3 h-3" />,
};

const ALLOWED_FILE_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function generateKey(label: string, existingKeys: string[]): string {
  let base = label
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '');

  // Ensure key starts with a letter
  base = base.replace(/^[^a-zA-Z]+/, '');

  // Ensure minimum length and max length (leave room for suffix)
  if (base.length < 2) base = 'optin';
  base = base.slice(0, 26);

  if (!existingKeys.includes(base)) return base;

  let suffix = 2;
  while (existingKeys.includes(`${base}_${suffix}`)) {
    suffix++;
  }
  return `${base}_${suffix}`;
}

const emptyOptin: Omit<OptinConfig, 'key' | 'order'> = {
  label: '',
  type: OptinType.TEXT,
  required: true,
  removable: true,
};

export function OptinsBuilder({ optins, onChange, eventId }: OptinsBuilderProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<OptinConfig>>(emptyOptin);
  const [isUploading, setIsUploading] = useState(false);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(optins);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    onChange(items.map((item, i) => ({ ...item, order: i })));
  };

  const openAddDialog = () => {
    setEditingIndex(null);
    setFormData({ ...emptyOptin });
    setDialogOpen(true);
  };

  const openEditDialog = (index: number) => {
    setEditingIndex(index);
    setFormData({ ...optins[index] });
    setDialogOpen(true);
  };

  const handleRemove = (index: number) => {
    const updated = optins.filter((_, i) => i !== index);
    onChange(updated.map((item, i) => ({ ...item, order: i })));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      alert('Tipo de arquivo inválido. Permitidos: PDF, PNG, JPG/JPEG, DOCX');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      alert('Arquivo excede o tamanho máximo de 10MB');
      return;
    }

    try {
      setIsUploading(true);
      const result = await queueApiService.uploadOptinDocument(eventId, file);
      setFormData((prev) => ({
        ...prev,
        documentUrl: result.url,
        documentKey: result.key,
        documentName: result.originalName,
      }));
    } catch {
      alert('Erro ao fazer upload do arquivo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveDialog = () => {
    if (!formData.label?.trim()) return;

    if (formData.type === OptinType.LINK && !formData.url?.trim()) return;
    if (formData.type === OptinType.DOCUMENT && !formData.documentUrl) return;

    if (editingIndex !== null) {
      const updated = [...optins];
      updated[editingIndex] = {
        ...updated[editingIndex],
        ...formData,
      } as OptinConfig;
      onChange(updated);
    } else {
      const existingKeys = optins.map((o) => o.key);
      const key = generateKey(formData.label!, existingKeys);
      const newOptin: OptinConfig = {
        key,
        label: formData.label!,
        type: formData.type || OptinType.TEXT,
        required: formData.required ?? true,
        removable: true,
        order: optins.length,
        content: formData.content,
        url: formData.url,
        documentUrl: formData.documentUrl,
        documentKey: formData.documentKey,
        documentName: formData.documentName,
      };
      onChange([...optins, newOptin]);
    }
    setDialogOpen(false);
  };

  const isAtLimit = optins.length >= 10;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-[16px] font-semibold text-foreground">Opt-ins</h3>
          <p className="text-sm text-muted-foreground">
            Configure os termos e aceites que os participantes devem concordar ao entrar na fila
          </p>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  onClick={openAddDialog}
                  disabled={isAtLimit}
                  size="sm"
                  className="flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Adicionar Opt-in
                </Button>
              </span>
            </TooltipTrigger>
            {isAtLimit && (
              <TooltipContent>
                <p>Limite de 10 opt-ins atingido</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>

      {optins.length === 0 ? (
        <div className="bg-card p-8 rounded-xl border border-border text-center">
          <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">Nenhum opt-in configurado</p>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="optins-list">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="space-y-2"
              >
                {optins.map((optin, index) => (
                  <Draggable
                    key={optin.key}
                    draggableId={optin.key}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`flex items-center gap-3 p-3 bg-card rounded-ds-card border border-border ${
                          snapshot.isDragging ? 'shadow-lg' : ''
                        }`}
                      >
                        <div
                          {...provided.dragHandleProps}
                          className="cursor-grab text-muted-foreground"
                        >
                          <GripVertical className="w-4 h-4" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-foreground truncate">
                              {optin.label}
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-brand-logo-1/10 text-brand-logo-1">
                              {TYPE_ICONS[optin.type]}
                              {TYPE_LABELS[optin.type]}
                            </span>
                            {optin.required && (
                              <span className="text-xs text-status-danger">obrigatório</span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEditDialog(index)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          {optin.removable === false ? (
                            <div className="h-8 w-8 flex items-center justify-center text-muted-foreground">
                              <Lock className="w-3.5 h-3.5" />
                            </div>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-status-danger hover:text-status-danger"
                              onClick={() => handleRemove(index)}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingIndex !== null ? 'Editar Opt-in' : 'Adicionar Opt-in'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Texto do checkbox *</Label>
              <Input
                value={formData.label || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, label: e.target.value }))}
                placeholder="Ex: Li e aceito os termos de privacidade"
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo de conteúdo</Label>
              <Select
                value={formData.type || OptinType.TEXT}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, type: value as OptinType }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={OptinType.TEXT}>Texto</SelectItem>
                  <SelectItem value={OptinType.LINK}>Link externo</SelectItem>
                  <SelectItem value={OptinType.DOCUMENT}>Documento (upload)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Conditional content fields */}
            {formData.type === OptinType.TEXT && (
              <div className="space-y-2">
                <Label>Conteúdo (opcional)</Label>
                <Textarea
                  value={formData.content || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
                  placeholder="Texto completo dos termos..."
                  rows={5}
                />
              </div>
            )}

            {formData.type === OptinType.LINK && (
              <div className="space-y-2">
                <Label>URL *</Label>
                <Input
                  type="url"
                  value={formData.url || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, url: e.target.value }))}
                  placeholder="https://example.com/termos"
                />
              </div>
            )}

            {formData.type === OptinType.DOCUMENT && (
              <div className="space-y-2">
                <Label>Documento *</Label>
                {formData.documentUrl ? (
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-ds-card border border-border">
                    <File className="w-4 h-4 text-brand-logo-1" />
                    <span className="text-sm text-foreground flex-1 truncate">
                      {formData.documentName || 'Documento enviado'}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          documentUrl: undefined,
                          documentKey: undefined,
                          documentName: undefined,
                        }))
                      }
                    >
                      Trocar
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-ds-card border-2 border-dashed border-border cursor-pointer hover:border-brand-logo-1 transition-colors">
                      <File className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {isUploading ? 'Enviando...' : 'Clique para enviar PDF, PNG, JPG ou DOCX (max 10MB)'}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.docx"
                      onChange={handleFileUpload}
                      disabled={isUploading}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label>Obrigatório</Label>
              <Switch
                checked={formData.required ?? true}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, required: checked }))
                }
                disabled={editingIndex !== null && optins[editingIndex]?.removable === false}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSaveDialog}
              disabled={
                !formData.label?.trim() ||
                (formData.type === OptinType.LINK && !formData.url?.trim()) ||
                (formData.type === OptinType.DOCUMENT && !formData.documentUrl) ||
                isUploading
              }
            >
              {editingIndex !== null ? 'Salvar' : 'Adicionar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
